import { AnimatePresence, div, motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import {
  FiUser,
  FiBook,
  FiDollarSign,
  FiRefreshCw,
  FiCheckCircle,
  FiClock,
  FiVideo,
  FiArrowRight,
  FiCalendar,
  FiTrendingUp,
} from "react-icons/fi";
import Loader from "../Live/Loader";
import axios from "axios";
import { API } from "../../shared/api/Api";
import StatItem from "../Statistics/StatItem";
import StatCard from "../../components/Statistics/StatCard";
import { useSocket } from "../../Providers/SocketProvider";
import { useNavigate } from "react-router-dom";
import { useCall } from "@stream-io/video-react-sdk";

// Reuse the same animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 120, damping: 14 },
  },
};

const fadeIn = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const sessionVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 20 },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
  hover: { scale: 1.02, y: -3, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" },
  tap: { scale: 0.98 },
};

const badgeVariants = {
  live: {
    scale: [1, 1.05, 1],
    transition: { repeat: Infinity, duration: 1.5 },
  },
  ended: { scale: 1 },
};

const DashboardStudent = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    enrolled_courses_count: 0,
    total_payments: 0,
    payments_verified: 0,
    payments_pending: 0,
    refunds_count: 0,
    active_courses: 0,
    pending_courses: 0,
    live_sessions_count: 0,
    attended_live_sessions: 0,
    recent_payments: [],
    live_sessions: [],
  });
  const { socket, isConnected } = useSocket();
  const navigate = useNavigate();
  const user = useSelector(selectUserInformations);
  const call = useCall();

  useEffect(() => {
    const fetchStatistics = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          API.Dashboard.app_statistics_statsDataForStudent,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        );
        console.log(response)
        if (response.data.status === 200) {
          setStatistics((prev) => ({
            ...prev,
            ...response.data.data,
            live_sessions: response.data.data.live_sessions.map((session) => ({
              ...session,
              status: session.ended_at ? "ended" : "live",
            })),
          }));
        }
      } catch (err) {
        console.log(err);
        setError("Failed to load student statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [user.token]);

  useEffect(() => {
    if (!socket) return;

    const handleLiveStatus = (data) => {
      setStatistics((prev) => {
        const sessionData = data;
        const existingIndex = prev.live_sessions.findIndex(
          (s) => s.id === sessionData.id
        );

        if (data.status === "live_started") {
          // Add new live session if not exists
          if (existingIndex === -1) {
            return {
              ...prev,
              live_sessions: [
                {
                  ...sessionData,
                  status: "live",
                  started_at: new Date(sessionData.started_at).toISOString(),
                  ended_at: null,
                  duration: 0,
                  attended: false,
                },
                ...prev.live_sessions,
              ],
            };
          }
        } else if (data.status === "live_ended") {
          // Update existing session with end data
          if (existingIndex !== -1) {
            const updatedSessions = [...prev.live_sessions];
            updatedSessions[existingIndex] = {
              ...updatedSessions[existingIndex],
              status: "ended",
              ended_at: sessionData.ended_at,
              duration: sessionData.duration,
            };

            return {
              ...prev,
              live_sessions: updatedSessions,
            };
          }
        }
        return prev;
      });
    };

    socket.on("live-status", handleLiveStatus);
    return () => socket.off("live-status", handleLiveStatus);
  }, [socket]);


  return (
    <div className=" min-h-screen p-4 sm:p-6 md:p-8">
      <div className="max-w-full mx-auto">
        {/* Welcome Banner with enhanced gradient */}
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="mb-8 border-0 rounded-2xl p-6 sm:p-8 shadow-lg bg-gradient-to-r from-[#134F98] via-[#1A65C9] to-[#2D7FE0] overflow-hidden relative"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -ml-10 -mb-10"></div>

          <div className="flex items-center gap-5 relative z-10">
            <div className="bg-white/20 p-4 rounded-xl">
              <FiUser className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Welcome Back!
              </h2>
              <p className="text-blue-100">
                {user?.gender === "male"
                  ? `Mr. ${user?.name}`
                  : `Ms. ${user?.name}`}
                , here's your learning overview
              </p>
            </div>
          </div>
        </motion.div>

        <div>
          {loading ? (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center items-center h-64"
              >
                <Loader />
              </motion.div>
            </AnimatePresence>
          ) : error ? (
            <AnimatePresence>
              <motion.div
                variants={fadeIn}
                className="text-red-500 text-center p-6 bg-white/80 backdrop-blur-sm rounded-xl shadow-md"
              >
                {error}
              </motion.div>
            </AnimatePresence>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              {/* Statistics Grid with improved spacing and responsiveness */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
                variants={containerVariants}
              >
                {/* Enrolled Courses Card */}
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <StatCard
                    title="Enrolled Courses"
                    value={statistics.enrolled_courses_count}
                    icon={FiBook}
                    bgColor="bg-gradient-to-br from-blue-50 to-blue-500"
                    iconColor="bg-blue-600"
                  >
                    <StatItem
                      label="Active"
                      value={statistics.active_courses}
                      color="text-green-600"
                    />
                    <StatItem
                      label="Pending"
                      value={statistics.pending_courses}
                      color="text-yellow-600"
                    />
                  </StatCard>
                </motion.div>

                {/* Payments Card */}
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <StatCard
                    title="Payments"
                    value={statistics.total_payments}
                    icon={FiDollarSign}
                    bgColor="bg-gradient-to-br from-purple-50 to-purple-500"
                    iconColor="bg-purple-600"
                  >
                    <StatItem
                      label="Verified"
                      value={statistics.payments_verified}
                      color="text-green-600"
                    />
                    <StatItem
                      label="Pending"
                      value={statistics.payments_pending}
                      color="text-yellow-600"
                    />
                  </StatCard>
                </motion.div>

                {/* Refunds Card */}
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <StatCard
                    title="Refunds"
                    value={statistics.refunds_count}
                    icon={FiRefreshCw}
                    bgColor="bg-gradient-to-br from-green-50 to-green-500"
                    iconColor="bg-green-600"
                  >
                    <StatItem
                      label="Refunded"
                      value={statistics.refunds_count}
                      color="text-yellow-600"
                    />
                  </StatCard>
                </motion.div>

                {/* Live Sessions Card */}
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <StatCard
                    title="Live Sessions"
                    value={statistics.live_sessions_count}
                    icon={FiVideo}
                    bgColor="bg-gradient-to-br from-yellow-50 to-yellow-500"
                    iconColor="bg-yellow-600"
                  >
                    <StatItem
                      label="Attended"
                      value={statistics.attended_live_sessions}
                      color="text-green-600"
                    />
                    <StatItem
                      label="Missed"
                      value={
                        statistics.live_sessions_count -
                        statistics.attended_live_sessions
                      }
                      color="text-red-600"
                    />
                  </StatCard>
                </motion.div>
              </motion.div>

              {/* New Live Sessions List with enhanced card design */}
              <motion.div
                variants={cardVariants}
                className="bg-white rounded-2xl shadow-lg p-6 mb-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FiVideo className="mr-2 text-blue-600" />
                    Recent Live Sessions
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {statistics.live_sessions?.length || 0} sessions
                  </span>
                </div>

                <div className="space-y-4 max-h-[320px] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar">
                  <AnimatePresence mode="wait">
                    {[...statistics.live_sessions]
                      .sort((a, b) => {
                        // Sort live sessions first
                        if (a.status === "live" && b.status !== "live")
                          return -1;
                        if (b.status === "live" && a.status !== "live")
                          return 1;

                        // Then sort by most recent first
                        return new Date(b.started_at) - new Date(a.started_at);
                      })
                      .map((session) => (
                        <motion.div
                          key={session.id}
                          variants={sessionVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          whileHover="hover"
                          whileTap="tap"
                          className={`p-5 border rounded-xl relative overflow-hidden transition-all duration-300 ${session.status === "live"
                            ? "border-green-300 bg-gradient-to-r from-green-50 to-green-100 shadow-md"
                            : "border-gray-200 bg-gray-50 hover:border-blue-200 hover:bg-blue-50/30"
                            }`}
                        >
                          {/* Animated status indicator bar */}
                          {session.status === "live" && (
                            <motion.div
                              className="absolute top-0 left-0 h-full w-1.5 bg-green-500"
                              variants={badgeVariants}
                              animate="live"
                            />
                          )}

                          <div className="flex items-center justify-between pl-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <div
                                  className={`p-2 rounded-lg ${session.status === "live"
                                    ? "bg-green-100"
                                    : "bg-gray-200"
                                    }`}
                                >
                                  <FiVideo
                                    className={`w-5 h-5 ${session.status === "live"
                                      ? "text-green-600"
                                      : "text-gray-600"
                                      }`}
                                  />
                                </div>
                                <h4 className="font-medium text-gray-800 text-lg">
                                  {session.course} - {session.lesson}
                                </h4>
                              </div>

                              <div className="ml-10 space-y-2">
                                <p className="text-sm text-gray-600 flex items-center">
                                  <FiUser className="mr-2 text-gray-400" />
                                  Instructor: {session.instructor_name}
                                </p>

                                <div className="flex items-center gap-4 flex-wrap">
                                  {session.status === "live" ? (
                                    <motion.span
                                      className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-2 w-fit"
                                      variants={badgeVariants}
                                    >
                                      <motion.span
                                        className="w-2 h-2 bg-green-600 rounded-full"
                                        animate={{
                                          scale: [1, 1.4, 1],
                                          opacity: [0.8, 1, 0.8],
                                        }}
                                        transition={{
                                          repeat: Infinity,
                                          duration: 1.5,
                                        }}
                                      />
                                      Live Now
                                    </motion.span>
                                  ) : (
                                    <div className="text-sm text-gray-500 flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                                      <FiClock className="w-4 h-4" />
                                      {session.duration} mins
                                    </div>
                                  )}

                                  <span
                                    className={`text-sm flex items-center gap-1 px-3 py-1 rounded-full ${session.attended
                                      ? "text-green-600 bg-green-50"
                                      : "text-red-600 bg-red-50"
                                      }`}
                                  >
                                    <FiCheckCircle className="w-4 h-4" />
                                    {session.attended ? "Attended" : "Missed"}
                                  </span>
                                </div>

                                {session.status === "ended" &&
                                  session.recording?.url && (
                                    <motion.a
                                      href={session.recording.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 w-fit group mt-2 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors"
                                      whileHover={{ x: 5 }}
                                      transition={{
                                        type: "spring",
                                        stiffness: 300,
                                      }}
                                    >
                                      <FiVideo className="w-4 h-4" />
                                      <span className="text-sm font-medium">
                                        Watch Session Record
                                      </span>
                                      <FiArrowRight className="w-4 h-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                                    </motion.a>
                                  )}
                              </div>
                            </div>

                            <div className="text-right ml-4">
                              {session.status === "live" ? (
                                <motion.button
                                  onClick={() => {
                                    const streamUrl = process.env.REACT_APP_FRONTEND_STREAM_URL || 'http://localhost:3001';
                                    const meetingUrl = `${streamUrl}/${session.meeting_code}?lesson=${session.lesson_id}`;
                                    window.open(meetingUrl, "_blank", "noopener,noreferrer");
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:shadow-green-200/50 transition-all font-medium"
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <FiVideo className="w-4 h-4" />
                                  Join Live
                                </motion.button>
                              ) : (
                                <div className="space-y-1 bg-gray-100 px-3 py-2 rounded-lg">
                                  <p className="text-sm text-gray-600 flex items-center justify-end">
                                    <FiCalendar className="w-4 h-4 mr-1" />
                                    {new Date(
                                      session.started_at
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </p>
                                  <p className="text-xs text-gray-500 flex items-center justify-end">
                                    <FiClock className="w-3 h-3 mr-1" />
                                    {new Date(
                                      session.started_at
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                  </AnimatePresence>

                  {statistics.live_sessions?.length === 0 && (
                    <motion.div
                      variants={cardVariants}
                      className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200"
                    >
                      <FiVideo className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                      <p>No live sessions found</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Check back later for upcoming sessions
                      </p>
                    </motion.div>
                  )}
                </div>
              </motion.div>

              {/* Recent Payments List with enhanced design */}
              <motion.div
                variants={cardVariants}
                className="bg-white rounded-2xl shadow-lg p-6 mt-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FiDollarSign className="mr-2 text-purple-600" />
                    Recent Payments
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {statistics.recent_payments?.length || 0} transactions
                  </span>
                </div>

                <div className="space-y-4 max-h-[240px] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar">
                  {statistics.recent_payments?.map((payment) => (
                    <motion.div
                      key={payment.id}
                      variants={cardVariants}
                      whileHover={{
                        y: -2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                      }}
                      className="p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-start space-x-3">
                          <div
                            className={`p-2 rounded-lg ${payment.status === "verified"
                              ? "bg-green-100"
                              : "bg-yellow-100"
                              }`}
                          >
                            <FiDollarSign
                              className={`w-5 h-5 ${payment.status === "verified"
                                ? "text-green-600"
                                : "text-yellow-600"
                                }`}
                            />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">
                              {payment.course}
                            </h4>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span
                                className={`text-sm font-medium px-2 py-0.5 rounded-md ${payment.status === "verified"
                                  ? "text-green-700 bg-green-100"
                                  : "text-yellow-700 bg-yellow-100"
                                  }`}
                              >
                                ${payment.amount}
                              </span>
                              <span className="text-sm text-gray-500 flex items-center">
                                <FiTrendingUp className="mr-1 w-3 h-3" />
                                {payment.method}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${payment.status === "verified"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                              }`}
                          >
                            {payment.status}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(payment.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {statistics.recent_payments?.length === 0 && (
                    <motion.div
                      variants={cardVariants}
                      className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200"
                    >
                      <FiDollarSign className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                      <p>No recent payments</p>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div >
  );
};

export default DashboardStudent;
